import type { Report } from '@reports/domain/entities';
import { createReportId } from '@reports/domain/entities';
import {
  ReportTypes,
  ReportInputTypes,
  ReportBlockTypes,
  ReportDataFormats,
  ReportChartTypes,
  ExportFormats,
} from '@reports/domain/types';
import {
  createTableBlockConfig,
  createChartBlockConfig,
  createReportFilterConfig,
  createExportConfig,
} from '@reports/domain/value-objects';
import { REPORT_ENDPOINTS } from '@reports/application/configs/endpoints';

import { REPORT_FILTERS_KEYS } from '../reportKeysConst';
import { REPORT_ACADEMY_LINKS } from '../reportAcademyConst';

export const salesReportDefinition: Report = {
  id: createReportId('sales-report'),
  type: ReportTypes.SALES,
  metadata: {
    name: 'reportSales:name',
    description: 'reportSales:description',
    tooltip: 'reportSales:tooltip',
    parrotAcademyLink: REPORT_ACADEMY_LINKS.SALES,
    filterDateRangeInfo: 'reportSales:filters.dateRangeInfo',
  },
  filters: [
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.PROVIDER,
      label: 'reportCommon:filterKeys.provider.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.provider.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.ORDER_TYPE,
      label: 'reportCommon:filterKeys.orderType.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.orderType.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.BRAND,
      label: 'reportCommon:filterKeys.brand.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.brand.placeholder',
    }),
  ],
  sections: [
    {
      key: 'chart',
      title: 'reportCommon:sections.charts.title',
      blocks: [
        createChartBlockConfig({
          key: 'salesByDay',
          title: 'reportSales:blocks.charts.salesByDay.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/3`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'xl',
          seriesConfig: [
            {
              key: 'sales_amount',
              label: 'reportSales:blocks.charts.salesByDay.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'averageTicketByDay',
          title: 'reportSales:blocks.charts.averageTicketByDay.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/5`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.LINE,
          width: 'md',
          seriesConfig: [
            {
              key: 'average_ticket_per_order',
              label: 'reportSales:blocks.charts.averageTicketByDay.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'averageTicketPerCustomerByDay',
          title: 'reportSales:blocks.charts.averageTicketPerCustomerByDay.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/6`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.LINE,
          width: 'md',
          seriesConfig: [
            {
              key: 'average_ticket_per_customer',
              label: 'reportSales:blocks.charts.averageTicketPerCustomerByDay.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'customerCountByDay',
          title: 'reportSales:blocks.charts.customerCountByDay.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/4`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'sm',
          seriesConfig: [
            {
              key: 'customer_count',
              label: 'reportSales:blocks.charts.customerCountByDay.series.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 0,
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'salesByOrderType',
          title: 'reportSales:blocks.charts.salesByOrderType.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/8`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'sm',
          seriesConfig: [
            {
              key: 'sales_amount',
              label: 'reportSales:blocks.charts.salesByOrderType.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'orderCountByOrderType',
          title: 'reportSales:blocks.charts.orderCountByOrderType.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/9`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'sm',
          seriesConfig: [
            {
              key: 'order_count',
              label: 'reportSales:blocks.charts.orderCountByOrderType.series.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 0,
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'salesByBrand',
          title: 'reportSales:blocks.charts.salesByBrand.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/10`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'md',
          seriesConfig: [
            {
              key: 'sales_amount',
              label: 'reportSales:blocks.charts.salesByBrand.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'salesByProvider',
          title: 'reportSales:blocks.charts.salesByProvider.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/7`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'md',
          seriesConfig: [
            {
              key: 'sales_amount',
              label: 'reportSales:blocks.charts.salesByProvider.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
      ],
    },
    {
      key: 'summary',
      title: 'reportCommon:sections.summary.title',
      blocks: [
        createTableBlockConfig({
          key: 'salesSummaryTable',
          endpoint: REPORT_ENDPOINTS.SALES.SUMMARY,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'sales_context',
              label: 'reportSales:blocks.summaryTable.columns.concept.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
              bold: true,
            },
            {
              key: 'sales_total',
              label: 'reportSales:blocks.summaryTable.columns.totalValue.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
          ],
          columnToggleEnabled: false,
        }),
      ],
    },
    {
      key: 'details',
      title: 'reportCommon:sections.details.title',
      blocks: [
        createTableBlockConfig({
          key: 'salesDetailsTable',
          endpoint: REPORT_ENDPOINTS.SALES.DETAILS,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'period',
              label: 'reportSales:blocks.detailsTable.columns.period.label',
              format: {
                type: ReportDataFormats.DATE,
              },
              align: 'left',
            },
            {
              key: 'sales_amount',
              label: 'reportSales:blocks.detailsTable.columns.salesAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'order_count',
              label: 'reportSales:blocks.detailsTable.columns.orderCount.label',
              format: {
                type: ReportDataFormats.NUMBER,
              },
              align: 'right',
            },
            {
              key: 'average_sales_per_order',
              label: 'reportSales:blocks.detailsTable.columns.averageSalesPerOrder.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'customer_count',
              label: 'reportSales:blocks.detailsTable.columns.customerCount.label',
              format: {
                type: ReportDataFormats.NUMBER,
              },
              align: 'right',
            },
            {
              key: 'average_sales_per_customer',
              label: 'reportSales:blocks.detailsTable.columns.averageSalesPerCustomer.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
          ],
          columnToggleEnabled: true,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
  ],
  export: createExportConfig({
    name: 'reportSales:export.name',
    endpoint: REPORT_ENDPOINTS.SALES.EXPORT,
    slugs: ['1', '2'],
    format: ExportFormats.EXCEL,
  }),
};
